import React, { useState, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { Box, Paper, Typography, useTheme, Stack, IconButton, Tooltip } from '@mui/material';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import MonacoEditor from '../../components/MonacoEditor';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import CodeIcon from '@mui/icons-material/Code';
import LinkIcon from '@mui/icons-material/Link';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

function MarkdownToHtml() {
  const theme = useTheme();
  const [markdown, setMarkdown] = useState('# Hello, world!\n\nThis is **Markdown** rendered live.');
  const editorRef = useRef(null);

  const handleResize = useCallback(() => {
    setTimeout(() => editorRef.current?.layout(), 0);
  }, []);

  const applyStyle = (style) => {
    const editor = editorRef.current;
    if (!editor) return;
    const selection = editor.getSelection();
    if (!selection) return;
    const selectedText = editor.getModel().getValueInRange(selection);
    let replacement;
    switch (style) {
      case 'bold':
        replacement = `**${selectedText}**`;
        break;
      case 'italic':
        replacement = `*${selectedText}*`;
        break;
      case 'code':
        replacement = `\`${selectedText}\``;
        break;
      case 'link':
        replacement = `[${selectedText}](url)`;
        break;
      default:
        replacement = selectedText;
    }
    editor.executeEdits('my-source', [{ range: selection, text: replacement }]);
  };

  return (
    <>
      <SEO
        title="Markdown Editor & HTML Preview | UmbrellaCo"
        description="Write Markdown with a live HTML preview. Free online Markdown editor with syntax highlighting, formatting toolbar, and instant rendering."
        keywords="markdown editor, online markdown, markdown to html, live preview markdown, writing tool"
        url="/text-tools/markdown"
        jsonLd={createSoftwareAppSchema(
          'Markdown Editor & HTML Preview',
          'Write and preview Markdown formatted text instantly in your browser.',
          'https://umbrellaco.in/text-tools/markdown',
          ['Markdown Editor', 'Text Formatting', 'Writing Tools']
        )}
      />
      <ToolPageContainer
        title="Markdown Editor"
        description="Write and format text using Markdown. View the rendered HTML preview live as you type."
        disablePadding
      >
      <PanelGroup direction="horizontal" onLayout={handleResize} style={{ height: '100%' }}>
        <Panel>
          <Paper sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1, px: 1 }}>
              <Tooltip title="Bold"><IconButton onClick={() => applyStyle('bold')}><FormatBoldIcon /></IconButton></Tooltip>
              <Tooltip title="Italic"><IconButton onClick={() => applyStyle('italic')}><FormatItalicIcon /></IconButton></Tooltip>
              <Tooltip title="Code"><IconButton onClick={() => applyStyle('code')}><CodeIcon /></IconButton></Tooltip>
              <Tooltip title="Link"><IconButton onClick={() => applyStyle('link')}><LinkIcon /></IconButton></Tooltip>
            </Stack>
            <Box sx={{ flexGrow: 1, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
              <MonacoEditor ref={editorRef} language="markdown" value={markdown} onChange={setMarkdown} />
            </Box>
          </Paper>
        </Panel>
        <PanelResizeHandle style={{ width: '10px' }} />
        <Panel>
          <Paper sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center' }}>Live Preview</Typography>
            <Box
              sx={{
                flexGrow: 1,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 2,
                overflowY: 'auto',
                '& h1, & h2, & h3, & p, & ul, & ol, & blockquote': {
                  margin: '0 0 1rem 0',
                },
                '& a': { color: 'primary.main' },
              }}
            >
              <ReactMarkdown>{markdown}</ReactMarkdown>
            </Box>
          </Paper>
        </Panel>
      </PanelGroup>
      </ToolPageContainer>
    </>
  );
}

export default MarkdownToHtml;